import {CanActivateChildFn, CanActivateFn, Routes} from '@angular/router';
import {MsalGuard} from '@azure/msal-angular';
import {ClcHome} from '../pages/clc-home/clc-home';
import {ClcUserAdmin} from '../pages/clc-user-admin/clc-user-admin';
import {ThemePreviewPage} from '../pages/theme-preview-page/theme-preview-page';
import {ClcCreateTaPage} from '../pages/clc-create-ta-page/clc-create-ta-page';
import {ClcReviewTaPage} from '../pages/clc-review-ta-page/clc-review-ta-page';
import {inject} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {TaAppStore} from '../../state/clcta-store';
import {ClcViewTaPage} from '../pages/clc-view-ta-page/clc-view-ta-page';
import {ClcManageTaPage} from '../pages/clc-manage-ta-page/clc-manage-ta-page';
import {ClcLocationsAdmin} from '../pages/clc-locations-admin/clc-locations-admin';
import {ClcAuditlogAdmin} from '../pages/clc-auditlog-admin/clc-auditlog-admin';
import {ClcApproverPage} from '../pages/clc-approver-page/clc-approver-page';
import {ClcManagerDashboardPage} from '../pages/clc-manager-dashboard-page/clc-manager-dashboard-page';
import {ClcFinanceDashboardPage} from '../pages/clc-finance-dashboard-page/clc-finance-dashboard-page';
import {ClcComplianceDashboardPage} from '../pages/clc-compliance-dashboard-page/clc-compliance-dashboard-page';
import {ClcAtoRatesAdmin} from '../pages/clc-ato-rates-admin/clc-ato-rates-admin';
import {ClcUnitsProjectsAdmin} from '../pages/clc-units-projects-admin/clc-units-projects-admin';
import {ClcSystemAdmin} from '../pages/clc-system-admin/clc-system-admin';
import {ClcReleasenotesPage} from '../pages/clc-releasenotes-page/clc-releasenotes-page';
import {ClcTemplatePage} from '../pages/clc-template-page/clc-template-page';
import {filter, map, take, timeout, catchError} from 'rxjs';
import {of} from 'rxjs';
import {minimumPermissionGuard} from './minimum-permission.guard';


const DevRouteGuard: CanActivateChildFn = () => {
  const store = inject(TaAppStore);
  return store.session().environmentName !== '';
}

const UserActiveGuard: CanActivateFn = () => {
  const store = inject(TaAppStore);

  // If session is already loaded, check immediately
  const session = store.session();
  if (session.details !== null) {
    const user = session.details?.user;
    return user !== null && user !== undefined && user.isActive === true;
  }

  // Otherwise wait for session to load
  return toObservable(store.session).pipe(
    filter(s => s.details !== null),
    take(1),
    map(s => {
      const user = s.details?.user;
      return user !== null && user !== undefined && user.isActive === true;
    }),
    timeout(30000),
    catchError(() => of(false)),
  );
}

// Read by BreadCrumbBarComponent. Only the pages where a blocked user would try to raise or
// submit a TA surface the account-blocked warning; elsewhere the bar stays clean.
const SHOW_BLOCKED = {showAccountBlocked: true};

export const routes: Routes = [
  {path:'', redirectTo: 'home', pathMatch: 'full'},
  {path:'home', component: ClcHome, canActivate: [MsalGuard], data: SHOW_BLOCKED},
  {path:'approvals', component: ClcApproverPage, canActivate: [MsalGuard, UserActiveGuard, minimumPermissionGuard] },
  {path:'manager-dashboard', component: ClcManagerDashboardPage, canActivate: [MsalGuard, UserActiveGuard, minimumPermissionGuard] },
  {path:'finance-dashboard', component: ClcFinanceDashboardPage, canActivate: [MsalGuard, UserActiveGuard, minimumPermissionGuard] },
  {path:'compliance-dashboard', component: ClcComplianceDashboardPage, canActivate: [MsalGuard, UserActiveGuard, minimumPermissionGuard] },
  {path:'admin/users', component: ClcUserAdmin, canActivate: [MsalGuard, UserActiveGuard, minimumPermissionGuard]},
  {path:'admin/locations', component: ClcLocationsAdmin, canActivate: [MsalGuard, UserActiveGuard, minimumPermissionGuard]},
  {path:'admin/clc-tiers', component: ClcAtoRatesAdmin, canActivate: [MsalGuard, UserActiveGuard, minimumPermissionGuard]},
  {path:'admin/auditlog', component: ClcAuditlogAdmin, canActivate: [MsalGuard, UserActiveGuard, minimumPermissionGuard]},
  {path:'admin/units-projects', component: ClcUnitsProjectsAdmin, canActivate: [MsalGuard, UserActiveGuard, minimumPermissionGuard]},
  {path:'admin/system', component: ClcSystemAdmin, canActivate: [MsalGuard, UserActiveGuard, minimumPermissionGuard]},
  {path:'ta/:id/view', component: ClcViewTaPage, canActivate: [MsalGuard]},
  {path:'ta/:id/manage', component: ClcManageTaPage, canActivate: [MsalGuard]},
  {path:'ta/:id/review', component: ClcReviewTaPage, canActivate: [MsalGuard], data: SHOW_BLOCKED},
  {path:'ta/:id', component: ClcCreateTaPage, canActivate: [MsalGuard], data: SHOW_BLOCKED},
  {path:'template/:id', component: ClcTemplatePage, canActivate: [MsalGuard]},
  {path:'releasenotes', component: ClcReleasenotesPage, canActivate: [MsalGuard]},
  {path:'debug/theme', component: ThemePreviewPage, canActivate: [MsalGuard, DevRouteGuard]},
  {path: '**', redirectTo: 'home'}
];
